import { createSelector } from 'reselect';

import { userSelector } from './user';
import { channelDefaultRecipientSelector } from './channel';

import { isLoggedInWithUnlimitedTransfer } from '@app/helper/user';
import { UNLIMITED_TRANSFER_LIMIT } from '@app/constant/sizes';

import { transformNewTransferToTransferListItem } from '@app/transformers/transfer';

// Reduce functions
const sentTransfersSelector = (state) => state.transfers.list;
const currentTransferSelector = (state) => state.transfers.transfer;
const transferSelector = (state) => state.transfer;
const transferId = (state, transferId) => transferId;

// Selectors
export const getAutoCompleteItems = createSelector(
  [sentTransfersSelector],
  (sentTransfers) =>
    sentTransfers
      .reduce((autoCompleteItems, transfer) => {
        const items = [...autoCompleteItems];

        // select transfer based on recipient email
        transfer.recipients.forEach((recipient) => {
          if (autoCompleteItems.indexOf(recipient.email) !== -1) return;
          items.push(recipient.email);
        });

        // select transfer based on file name
        transfer.files.forEach((file) => {
          if (autoCompleteItems.indexOf(file.name) !== -1) return;
          items.push(file.name);
        });
        return items;
      }, [])
      .map((item) => ({ label: item, id: item, spellings: item }))
);

export const isSendingToDefaultRecipientSelector = createSelector(
  transferSelector,
  channelDefaultRecipientSelector,
  (transfer, channelDefaultRecipient) => {
    if (!channelDefaultRecipient) {
      return false;
    }

    if (transfer.recipients.length !== 1) {
      return false;
    }

    if (transfer.type !== 'email') {
      return false;
    }

    if (transfer.recipients[0] !== channelDefaultRecipient) {
      return false;
    }

    return true;
  }
);

export const allowedSizeSelector = createSelector(
  isSendingToDefaultRecipientSelector,
  userSelector,
  (isSendingToDefaultRecipient, user) => {
    if (isLoggedInWithUnlimitedTransfer(user)) {
      return UNLIMITED_TRANSFER_LIMIT;
    }

    const { transferLimitForDefaultRecipient, transferLimit } = user.rules;

    if (isSendingToDefaultRecipient) {
      return transferLimitForDefaultRecipient;
    }

    return transferLimit;
  }
);

// Get any type of transfer by Id (currently not supporting received transfers)
export const getTransferById = createSelector(
  [
    sentTransfersSelector,
    currentTransferSelector,
    transferSelector,
    transferId,
  ],
  (sentTransfers, currentTransfer, newTransfer, transferId) => {
    if (!transferId) {
      return undefined;
    }

    if (currentTransfer && currentTransfer.id === transferId) {
      return currentTransfer;
    }

    const listTransfer = (sentTransfers || []).find(
      ({ id }) => id === transferId
    );

    if (listTransfer) {
      return listTransfer;
    }

    if (newTransfer && newTransfer.id === transferId) {
      return transformNewTransferToTransferListItem(newTransfer);
    }

    return undefined;
  }
);
